import { REVERSE_SCHOOL_LEVEL_MAP } from '../../types/enum-maps';
import { mapSchoolLevelsToEducationLevels } from '../../core/levels/level-catalog';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Auth } from '../../core/firebase/auth';
import { firstValueFrom } from 'rxjs';
import { SubscriptionType } from 'src/app/types/subscription';
import { OrderProcessOrderingComponent } from './order-process-ordering/order-process-ordering.component';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { OrderProcessProductsComponent } from './order-process-products/order-process-products.component';
import { UpgradeRoleSelectorComponent } from './upgrade/upgrade-role-selector/upgrade-role-selector.component';
import { UpgradeSchoolUnitStep4Component } from './upgrade/upgrade-school-unit-step4/upgrade-school-unit-step4.component';
import { UpgradeSchoolAuthorityStep4Component } from './upgrade/upgrade-school-authority-step4/upgrade-school-authority-step4.component';
import { UpgradeSummaryComponent } from './upgrade/upgrade-summary/upgrade-summary.component';
import { UpgradeOrderingAuthorityComponent } from './upgrade/upgrade-ordering-authority/upgrade-ordering-authority.component';
import { UpgradeOrderingUnitComponent } from './upgrade/upgrade-ordering-unit/upgrade-ordering-unit.component';
import { SchoolApiService } from 'src/app/services/school-api.service';
import { SchoolAuthorityApiService } from 'src/app/services/school-authority-api.service';
import { BillingOrganisation } from 'src/app/services/billing-profile-api.service';
import { LicenseApiService } from 'src/app/services/license-api.service';
import { StreamUserService } from 'src/modules/chat/services/stream-user.service';
import { ProfileService } from 'src/app/services/profile.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { RoleService } from 'src/app/services/role.service';
import { NotificationService } from 'src/app/services/notification.service';
import { refreshFirebaseIdToken } from 'src/app/core/firebase-auth-session';
import { markSchuleinheitPaidPlanSidebarAcknowledged } from 'src/app/core/schuleinheit-pro-sidebar-flag';
import {
  hasCoordinates,
  mapGeoLocationToLocationReq,
} from 'src/app/utils/location-mapper';
import { LocationsService } from 'src/app/services/locations.service';
import { normalizeWebsiteUrl } from 'src/app/utils/website-url';
import { clearNegotiatedOfferSelection } from 'src/app/pages/onboarding/municipality-plan-selection';
import {
  EducationLevelEnum,
  SchoolLevelEnum,
  SchoolTypeEnum,
  CreateSchoolReq,
  CreateSchoolAuthorityReq,
  UserBillDetail,
} from 'src/app/types/rest-api';
import {
  SchoolTypes,
  SchoolLevels,
  SchoolLevelDescriptions,
} from 'src/app/types/school';
import {
  clearFachkraftUpgradeOrderProcessDraft,
  loadFachkraftUpgradeOrderProcessDraft,
  saveFachkraftUpgradeOrderProcessDraft,
  type FachkraftUpgradeStep4Snapshot,
} from './fachkraft-upgrade-draft';

export type OrderProcessComponentProps = {
  page?: number;
  type?: SubscriptionType;
  /**
   * Schuleinheit user already has a school (e.g. free tier): skip Fachkraft role cards
   * and open directly at plan selection (school onboarding «Angebot» equivalent).
   * After paid checkout, no second school is created — only subscription is ordered.
   */
  schuleinheitExistingSchoolId?: string;
};

type UpgradeRole = 'schuleinheit' | 'schulgemeinde';

type DialogStage =
  | 'memberBlocked'
  | 'roleSelect'
  | 'offers'
  | 'ordering'
  | 'upgradeSchoolUnit'
  | 'upgradeSchoolAuthority'
  | 'upgradeSummary'
  | 'upgradeOffers'
  | 'upgradeOrderingAuthority'
  | 'upgradeOrderingUnit'
  | 'upgradeOrderSuccess';

const SCHOOL_TYPE_TO_API: Record<(typeof SchoolTypes)[number], SchoolTypeEnum> =
  {
    'Öffentliche Schule': 'ÖFFENTLICHE SCHULE',
    Privatschule: 'PRIVATSCHULE',
    Sonderschule: 'SONDERSCHULE',
  };

@Component({
  selector: 'app-order-process',
  templateUrl: './order-process.component.html',
  styleUrls: ['./order-process.component.scss'],
  imports: [
    MatIcon,
    MatButton,
    OrderProcessOrderingComponent,
    OrderProcessProductsComponent,
    UpgradeRoleSelectorComponent,
    UpgradeSchoolUnitStep4Component,
    UpgradeSchoolAuthorityStep4Component,
    UpgradeSummaryComponent,
    UpgradeOrderingAuthorityComponent,
    UpgradeOrderingUnitComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class OrderProcessComponent implements OnInit {
  data = inject<OrderProcessComponentProps>(MAT_DIALOG_DATA);
  private router = inject(Router);
  private auth = inject(Auth);
  private schoolApiService = inject(SchoolApiService);
  private schoolAuthorityApiService = inject(SchoolAuthorityApiService);
  private licenseApiService = inject(LicenseApiService);
  private streamUserService = inject(StreamUserService);
  private profileService = inject(ProfileService);
  private permissionsService = inject(PermissionsService);
  private roleService = inject(RoleService);
  private notificationService = inject(NotificationService);
  private locationsService = inject(LocationsService);
  private cdr = inject(ChangeDetectorRef);

  view: DialogStage = 'roleSelect';
  startedAtOrdering = false;

  @ViewChild('stepperContainer') public stepperContainer:
    | OrderProcessOrderingComponent
    | undefined;

  page = 0;
  selectedOffer: SubscriptionType | null = null;
  upgradeRole: UpgradeRole | null = null;
  upgradeSummaryData: any = null;
  createdId: string | null = null;
  loading = false;
  /**
   * `true`, wenn der eingelöste Promo-Code des aktuellen Upgrades die ersten
   * N Monate gratis macht. Steuert den Rechnungs-Hinweistext auf der
   * `upgradeOrderSuccess`-View.
   */
  firstMonthsFreePromo = false;

  /** Resume «Upgrade auf Schul-Account» step 4 forms from session. */
  lastSchuleinheitStep4: FachkraftUpgradeStep4Snapshot | null = null;
  lastSchulgemeindeStep4: FachkraftUpgradeStep4Snapshot | null = null;

  /** «Bestehende Schule»: free Schuleinheit upgrading to paid; see {@link OrderProcessComponentProps.schuleinheitExistingSchoolId}. */
  existingSchuleinheitSchoolId: string | null = null;

  get existingSchuleinheitBillingOrganisation(): BillingOrganisation | null {
    return this.existingSchuleinheitSchoolId
      ? { kind: 'school', id: this.existingSchuleinheitSchoolId }
      : null;
  }

  /**
   * Stable idempotency key for this dialog's purchase. Reused across retries of the
   * same order so the backend replays the result instead of charging again; a new
   * dialog (new purchase) gets a fresh key.
   */
  private readonly orderIdempotencyKey = crypto.randomUUID();

  readonly dialogRef = inject(MatDialogRef<OrderProcessComponentProps>);

  constructor() {
    // Invited admins of someone else's organisation are covered by the owner's
    // subscription and cannot purchase (backend rejects with MEMBER_CANNOT_PURCHASE).
    // Show the hint instead of any shop entry point.
    if (this.roleService.loaded && this.roleService.isInvitedMemberOnly) {
      this.view = 'memberBlocked';
      return;
    }

    const existingId = this.data?.schuleinheitExistingSchoolId;
    if (existingId) {
      this.existingSchuleinheitSchoolId = existingId;
      this.view = 'upgradeOffers';
      this.upgradeRole = 'schuleinheit';
      this.selectedOffer = null;
      this.page = 0;
      return;
    }

    const pageFromData = this.data?.page;

    if (pageFromData === 1 && this.data?.type) {
      this.view = 'ordering';
      this.selectedOffer = this.data.type;
      this.page = pageFromData;
      this.startedAtOrdering = true;
    } else if (pageFromData === 0) {
      this.view = 'offers';
      this.selectedOffer = this.data?.type ?? null;
      this.page = pageFromData;
    } else {
      // No explicit entry point: if the user already owns a Schuleinheit school,
      // attach the order to it (upgrade flow) instead of offering to create a new
      // school. Creating a second school fails the backend limit check with 403,
      // which is what drove users to re-order (and re-purchase) repeatedly.
      const upgradeSchoolId = this.roleService.loaded
        ? this.roleService.getSchuleinheitUpgradeSchoolId()
        : null;
      if (upgradeSchoolId) {
        this.existingSchuleinheitSchoolId = upgradeSchoolId;
        this.view = 'upgradeOffers';
        this.upgradeRole = 'schuleinheit';
        this.selectedOffer = null;
        this.page = 0;
        return;
      }
      this.view = 'roleSelect';
      this.selectedOffer = null;
      this.page = 0;
    }
  }

  ngOnInit(): void {
    if (this.view === 'memberBlocked') {
      return;
    }
    if (this.existingSchuleinheitSchoolId) {
      return;
    }
    if (this.data?.page !== undefined && this.data?.page !== null) {
      return;
    }
    const uid = this.auth.currentUser?.uid;
    const raw = loadFachkraftUpgradeOrderProcessDraft();
    if (!raw || !uid || raw.firebaseUid !== uid) {
      if (raw && (!uid || raw.firebaseUid !== uid)) {
        clearFachkraftUpgradeOrderProcessDraft();
      }
      return;
    }
    this.view = raw.view;
    this.upgradeRole = raw.upgradeRole;
    this.upgradeSummaryData = raw.upgradeSummaryData;
    this.createdId = raw.createdId;
    this.selectedOffer = raw.selectedOffer;
    this.page = raw.page;
    this.startedAtOrdering = raw.startedAtOrdering;
    this.lastSchuleinheitStep4 = raw.schuleinheitStep4 ?? null;
    this.lastSchulgemeindeStep4 = raw.schulgemeindeStep4 ?? null;
  }

  private persistFachkraftUpgradeDraft(): void {
    const view = this.view;
    if (view === 'memberBlocked') {
      return;
    }
    if (this.existingSchuleinheitSchoolId) {
      return;
    }
    const uid = this.auth.currentUser?.uid;
    if (!uid) return;
    if (this.data?.page !== undefined && this.data?.page !== null) {
      return;
    }
    saveFachkraftUpgradeOrderProcessDraft({
      firebaseUid: uid,
      view,
      upgradeRole: this.upgradeRole,
      upgradeSummaryData: this.upgradeSummaryData,
      createdId: this.createdId,
      selectedOffer: this.selectedOffer,
      page: this.page,
      startedAtOrdering: this.startedAtOrdering,
      schuleinheitStep4: this.lastSchuleinheitStep4 ?? undefined,
      schulgemeindeStep4: this.lastSchulgemeindeStep4 ?? undefined,
    });
  }

  private clearFachkraftUpgradeDraft(): void {
    clearFachkraftUpgradeOrderProcessDraft();
    this.lastSchuleinheitStep4 = null;
    this.lastSchulgemeindeStep4 = null;
  }

  onSchuleinheitStep4Draft(s: FachkraftUpgradeStep4Snapshot): void {
    this.lastSchuleinheitStep4 = s;
    this.persistFachkraftUpgradeDraft();
  }

  onSchulgemeindeStep4Draft(s: FachkraftUpgradeStep4Snapshot): void {
    this.lastSchulgemeindeStep4 = s;
    this.persistFachkraftUpgradeDraft();
  }

  onPrev() {
    switch (this.view) {
      case 'upgradeSummary':
        this.view =
          this.upgradeRole === 'schuleinheit'
            ? 'upgradeSchoolUnit'
            : 'upgradeSchoolAuthority';
        this.persistFachkraftUpgradeDraft();
        return;
      case 'upgradeSchoolUnit':
        this.view = 'roleSelect';
        this.persistFachkraftUpgradeDraft();
        return;
      case 'upgradeSchoolAuthority':
        this.view =
          this.upgradeRole === 'schulgemeinde'
            ? 'upgradeOrderSuccess'
            : 'roleSelect';
        this.persistFachkraftUpgradeDraft();
        return;
      case 'roleSelect':
        this.onCloseDialog();
        return;
      case 'ordering': {
        if (this.stepperContainer?.getCurrentStep() === 0) {
          this.view = 'offers';
        } else {
          this.stepperContainer?.onPrev();
        }
        this.persistFachkraftUpgradeDraft();
        return;
      }
      case 'upgradeOffers':
        // Fresh Fachkraft → Schulgemeinde: roleSelect war direkt davor.
        if (this.upgradeRole === 'schulgemeinde' && this.createdId == null) {
          this.view = 'roleSelect';
        } else if (
          // Fresh Fachkraft → Schuleinheit: kam aus upgradeSummary (createProfile).
          this.upgradeRole === 'schuleinheit' &&
          !this.existingSchuleinheitSchoolId
        ) {
          this.view = 'upgradeSummary';
        } else {
          // Schuleinheit von bestehender Schule (kein vorheriger Screen) /
          // Schulgemeinde nach License-Order: Dialog schliessen.
          this.onCloseDialog();
          return;
        }
        this.persistFachkraftUpgradeDraft();
        return;
      case 'upgradeOrderingAuthority':
        this.view = 'upgradeOffers';
        this.persistFachkraftUpgradeDraft();
        return;
      case 'upgradeOrderingUnit':
        this.view = 'upgradeOffers';
        this.persistFachkraftUpgradeDraft();
        return;
      case 'upgradeOrderSuccess':
        if (this.upgradeRole === 'schulgemeinde' && this.createdId == null) {
          this.view = 'upgradeOrderingAuthority';
        } else if (
          this.upgradeRole === 'schuleinheit' &&
          this.createdId != null
        ) {
          this.view = 'upgradeOrderingUnit';
        } else {
          this.view = 'upgradeOffers';
        }
        this.persistFachkraftUpgradeDraft();
        return;
      default:
        break;
    }
    this.persistFachkraftUpgradeDraft();
  }

  onCloseDialog() {
    // An offer picked but not ordered must not leak into the onboarding wizard,
    // which reads the same session entry. The ordering step clears it on success.
    clearNegotiatedOfferSelection();
    this.persistFachkraftUpgradeDraft();
    this.dialogRef.close();
  }

  /**
   * Only the two Fachkraft paths that order *before* their organisation exists:
   * the deal's order is written without an organisation and is adopted when one
   * is created. An existing organisation would never adopt it.
   */
  showNegotiatedOfferForDialog(): boolean {
    if (this.view !== 'upgradeOffers') return false;
    if (this.upgradeRole === 'schulgemeinde') return this.createdId == null;
    if (this.upgradeRole === 'schuleinheit') {
      return !this.existingSchuleinheitSchoolId;
    }
    return false;
  }

  /**
   * After Fachkraft → Schuleinheit success, send user to their new school profile
   * (`/schools/:id`), not the dashboard, when `schoolProfileId` is known.
   */
  private async goHomeAfterRoleRefresh(
    schoolProfileId?: string | null
  ): Promise<void> {
    this.clearFachkraftUpgradeDraft();
    this.roleService.resetActiveContext();
    this.profileService.clearCurrentUserCache();
    await firstValueFrom(this.roleService.loadRole());
    if (schoolProfileId) {
      await this.router.navigate(['/schools', schoolProfileId]);
      return;
    }
    await this.router.navigateByUrl('/home');
  }

  async onSelectOffer(type: SubscriptionType) {
    this.selectedOffer = type;

    // Schulgemeinde upgrade: Order-Abschluss-Seite zeigen (außer bei FREE)
    if (this.upgradeRole === 'schulgemeinde' && this.createdId == null) {
      if (type === 'SCHOOL_FREE') {
        this.view = 'upgradeOrderSuccess';
      } else {
        this.view = 'upgradeOrderingAuthority';
      }
      this.persistFachkraftUpgradeDraft();
      return;
    }

    /** Schuleinheit: school + license orders only after plan selection (see `onSchuleinheitUnitOrderCompleted`). */
    if (this.upgradeRole === 'schuleinheit') {
      if (type === 'SCHOOL_FREE') {
        if (this.existingSchuleinheitSchoolId) {
          this.notificationService.error(
            'Du nutzt bereits die kostenlose Schuleinheit-Version. Wähle ein kostenpflichtiges Abo.'
          );
          return;
        }
        await this.finalizeSchuleinheitFreeFromOffers();
      } else {
        this.view = 'upgradeOrderingUnit';
        this.persistFachkraftUpgradeDraft();
      }
      return;
    }

    if (type === 'SCHOOL_FREE') {
      this.dialogRef.close();
      await this.goHomeAfterRoleRefresh();
      return;
    }

    if (this.createdId != null) {
      this.persistFachkraftUpgradeDraft();
      this.dialogRef.close();
      await this.router.navigate(['/checkout/mock'], {
        queryParams: {
          plan: type,
          schoolId: this.createdId,
          schoolName: this.upgradeSummaryData?.form?.schoolName || '',
          schoolType: this.upgradeSummaryData?.form?.schoolType || '',
          fromOnboarding: 'true',
        },
      });
      return;
    }

    this.selectedOffer = type;
    this.view = 'ordering';
    this.persistFachkraftUpgradeDraft();
  }

  onSelectUpgradeRole(role: UpgradeRole) {
    this.selectedOffer = null;
    this.upgradeRole = role;
    if (role === 'schulgemeinde') {
      this.view = 'upgradeOffers';
    } else {
      this.view = 'upgradeSchoolUnit';
    }
    this.persistFachkraftUpgradeDraft();
  }

  onOrderCompleted() {
    this.view = 'upgradeOrderSuccess';
    this.persistFachkraftUpgradeDraft();
  }

  /**
   * After «Bestellung abschließen»: Fachkraft flow creates school + entitlement;
   * existing Schuleinheit upgrade only attaches subscription via license API (already done in ordering step).
   */
  async onSchuleinheitUnitOrderCompleted(bill: UserBillDetail) {
    if (this.existingSchuleinheitSchoolId) {
      this.loading = true;
      this.cdr.detectChanges();
      try {
        this.createdId = this.existingSchuleinheitSchoolId;
        this.profileService.clearCurrentUserCache();
        await firstValueFrom(this.roleService.loadRole()).catch(() => null);
        await refreshFirebaseIdToken(this.auth).catch(() => null);
        markSchuleinheitPaidPlanSidebarAcknowledged(
          this.existingSchuleinheitSchoolId!
        );
        this.view = 'upgradeOrderSuccess';
        this.persistFachkraftUpgradeDraft();
      } finally {
        this.loading = false;
        this.cdr.detectChanges();
      }
      return;
    }

    const raw = this.upgradeSummaryData?.form;
    const location = this.upgradeSummaryData?.location;
    if (!raw) {
      this.notificationService.error(
        'Keine Profildaten gefunden. Bitte kehre zur Zusammenfassung zurück.'
      );
      return;
    }
    this.loading = true;
    this.cdr.detectChanges();
    try {
      const orderNumber = await firstValueFrom(
        this.licenseApiService.resolveOrderNumberForCreateSchool(
          bill,
          { ensureSubscriptionIfMissing: true },
          this.orderIdempotencyKey
        )
      );
      const schoolPayload: CreateSchoolReq = {
        name: raw?.schoolName || '',
        school_type:
          SCHOOL_TYPE_TO_API[raw?.schoolType as (typeof SchoolTypes)[number]] ||
          'ÖFFENTLICHE SCHULE',
        order_number: orderNumber,
      };
      const res = await firstValueFrom(
        this.schoolApiService.createSchool(schoolPayload)
      );
      this.createdId = res.id;
      markSchuleinheitPaidPlanSidebarAcknowledged(res.id);
      void this.runBackgroundTasks(
        this.createdId,
        'schuleinheit',
        raw,
        location
      );
      this.view = 'upgradeOrderSuccess';
      this.persistFachkraftUpgradeDraft();
    } catch (e: any) {
      console.error('Schuleinheit upgrade: school creation failed:', e);
      this.notificationService.error(
        e?.message ||
          'Die Schule konnte nicht erstellt werden. Bitte versuche es erneut.'
      );
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * «Kostenlos» auf dem Angebotsschritt: nur Schule anlegen — kein Lizenz-Order, kein Stripe.
   * Die initiale Fachkraft-Entitlement (`school_unit_creation_left=1` aus
   * `user_service.create_account`) erlaubt `POST /v1/schools/create` ohne `SCHOOL_UNIT_SUBSCRIPTION`-Order.
   * Würden wir hier `ensureSchoolUnitSubscriptionEntitlement` aufrufen, erzeugte das Backend
   * (`license_service.create_license_order`, has_main_subscription→true) zwingend ein Stripe-Abo.
   */
  private async finalizeSchuleinheitFreeFromOffers() {
    const raw = this.upgradeSummaryData?.form;
    const location = this.upgradeSummaryData?.location;
    if (!raw) {
      return;
    }
    this.loading = true;
    this.cdr.detectChanges();
    try {
      const schoolPayload: CreateSchoolReq = {
        name: raw?.schoolName || '',
        school_type:
          SCHOOL_TYPE_TO_API[raw?.schoolType as (typeof SchoolTypes)[number]] ||
          'ÖFFENTLICHE SCHULE',
      };
      const res = await firstValueFrom(
        this.schoolApiService.createSchool(schoolPayload)
      );
      this.createdId = res.id;
      await this.runBackgroundTasks(
        this.createdId,
        'schuleinheit',
        raw,
        location
      );
      this.dialogRef.close();
      await this.goHomeAfterRoleRefresh(this.createdId);
    } catch (e: any) {
      console.error('Schuleinheit (free) finalize failed:', e);
      this.notificationService.error(
        e?.message ||
          'Die Schule konnte nicht erstellt werden. Bitte versuche es erneut.'
      );
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  async onOrderSuccessContinue() {
    // Schuleinheit: school exists; land on Schulprofil (edit mode), not the dashboard.
    if (this.upgradeRole === 'schuleinheit') {
      this.dialogRef.close();
      await this.goHomeAfterRoleRefresh(this.createdId ?? null);
      return;
    }
    this.view = 'upgradeSchoolAuthority';
    this.persistFachkraftUpgradeDraft();
  }

  onStep4Submitted(data: any) {
    this.upgradeSummaryData = data;
    this.view = 'upgradeSummary';
    this.persistFachkraftUpgradeDraft();
  }

  onSummaryEdit() {
    this.view =
      this.upgradeRole === 'schuleinheit'
        ? 'upgradeSchoolUnit'
        : 'upgradeSchoolAuthority';
    this.persistFachkraftUpgradeDraft();
  }

  async onSummaryCreate() {
    if (this.loading) return;
    this.loading = true;
    this.cdr.detectChanges();

    const raw = this.upgradeSummaryData?.form;
    const location = this.upgradeSummaryData?.location;

    try {
      // Schuleinheit: «Zusammenfassung» only confirms profile data. License `GET/POST /orders`
      // and `POST /schools` run after «Bestellung abschliessen» (or from «Kostenlos» on the offer step).
      if (this.upgradeRole === 'schuleinheit') {
        this.loading = false;
        this.view = 'upgradeOffers';
        this.selectedOffer = null;
        this.cdr.detectChanges();
        this.persistFachkraftUpgradeDraft();
        return;
      }

      if (this.upgradeRole === 'schulgemeinde') {
        const schoolTypeApi =
          SCHOOL_TYPE_TO_API[raw?.schoolType as (typeof SchoolTypes)[number]] ||
          'ÖFFENTLICHE SCHULE';
        const authorityPayload: CreateSchoolAuthorityReq = {
          name: raw?.schoolName || '',
          school_type: schoolTypeApi,
        };
        const res = await firstValueFrom(
          this.schoolAuthorityApiService.create(authorityPayload)
        );
        this.createdId = res.id;
      }

      if (!this.createdId) {
        throw new Error('Entität konnte nicht erstellt werden (ID fehlt).');
      }

      await this.runBackgroundTasks(
        this.createdId,
        this.upgradeRole!,
        raw,
        location
      );
      this.loading = false;
      this.cdr.detectChanges();
      this.clearFachkraftUpgradeDraft();
      this.dialogRef.close();
      await this.router.navigateByUrl('/home');
    } catch (criticalError: any) {
      console.error('CRITICAL UPGRADE FAILURE:', criticalError);
      this.loading = false;
      this.cdr.detectChanges();

      this.notificationService.error(
        criticalError.message ||
          'Die Erstellung ist fehlgeschlagen. Bitte versuche es später erneut.'
      );
    }
  }

  private async runBackgroundTasks(
    id: string,
    role: UpgradeRole,
    raw: any,
    location: any
  ) {
    try {
      if (location && !hasCoordinates(location)) {
        location =
          (await this.locationsService.withCoordinates(location)) ?? location;
      }
      const me = await firstValueFrom(this.profileService.getMe(false)).catch(
        () => null
      );
      const adminUserId = me?.id || this.auth.currentUser?.uid;

      if (role === 'schuleinheit') {
        // Cache perms locally
        const perms = this.permissionsService.getPermissions() ?? [];
        if (!perms.some(p => p.schoolId === id)) {
          perms.push({ id, schoolId: id, userIds: [], users: [] });
          this.permissionsService.setPermissions(perms);
        }

        const levels = mapSchoolLevelsToEducationLevels(raw?.levels);

        const address = mapGeoLocationToLocationReq(location);

        await Promise.allSettled([
          firstValueFrom(
            this.schoolApiService.updateInfo(id, {
              name: raw.schoolName || '',
              school_type:
                SCHOOL_TYPE_TO_API[
                  raw.schoolType as (typeof SchoolTypes)[number]
                ] || 'ÖFFENTLICHE SCHULE',
              education_levels: levels,
            })
          ),
          address
            ? firstValueFrom(
                this.schoolApiService.updateBasicInfo(id, {
                  institution_id: raw.institutionId || null,
                  count_employees: Number(raw.countEmployees ?? 0),
                  count_students: Number(raw.countStudents ?? 0),
                  address,
                  website: normalizeWebsiteUrl(raw.website),
                })
              )
            : Promise.resolve(),
          raw.about
            ? firstValueFrom(
                this.schoolApiService.updateAboutUs(id, {
                  about: raw.about.trim(),
                  about_html: `<p>${this.escapeHtml(raw.about.trim())}</p>`,
                })
              )
            : Promise.resolve(),
          adminUserId
            ? firstValueFrom(
                this.schoolApiService.updateContacts({
                  schools: [
                    {
                      school_id: id,
                      contacts: [
                        {
                          user_id: adminUserId,
                          email: (raw.contactEmail || '').trim(),
                          title: (raw.contactName || '').trim() || 'Admin',
                          phone: (raw.contactPhone || '').trim(),
                        },
                      ],
                    },
                  ],
                })
              )
            : Promise.resolve(),
        ]);
        await firstValueFrom(this.schoolApiService.publishSchool(id)).catch(
          () => null
        );

        if (me) {
          const fullName =
            (me.name || '').trim() ||
            (raw?.contactName || '').trim() ||
            'Admin';
          await firstValueFrom(
            this.streamUserService.createUser({
              name: fullName,
              username: fullName,
              language: 'de',
            })
          ).catch(() => null);
        }
      } else if (role === 'schulgemeinde') {
        const levels = mapSchoolLevelsToEducationLevels(raw?.levels);

        const schoolLevels = (raw?.levelDescriptions || [])
          .map((d: any) => REVERSE_SCHOOL_LEVEL_MAP[d])
          .filter((v: any): v is SchoolLevelEnum => !!v);

        const address = mapGeoLocationToLocationReq(location);
        const schoolTypeApi =
          SCHOOL_TYPE_TO_API[raw?.schoolType as (typeof SchoolTypes)[number]] ||
          'ÖFFENTLICHE SCHULE';

        await Promise.allSettled([
          firstValueFrom(
            this.schoolAuthorityApiService.updateInfo(id, {
              name: raw.schoolName || '',
              school_type: schoolTypeApi,
              education_levels: levels,
              ...(schoolLevels.length ? { school_levels: schoolLevels } : {}),
            })
          ),
          address
            ? firstValueFrom(
                this.schoolAuthorityApiService.updateBasicInfo(id, {
                  institution_id: raw.institutionId || null,
                  count_employees: Number(raw.countEmployees ?? 0),
                  count_students: Number(raw.countStudents ?? 0),
                  address,
                  website: normalizeWebsiteUrl(raw.website),
                })
              )
            : Promise.resolve(),
          raw.about
            ? firstValueFrom(
                this.schoolAuthorityApiService.updateAboutUs(id, {
                  about: raw.about.trim(),
                  about_html: `<p>${this.escapeHtml(raw.about.trim())}</p>`,
                })
              )
            : Promise.resolve(),
          adminUserId
            ? firstValueFrom(
                this.schoolAuthorityApiService.updateContacts({
                  school_authorities: [
                    {
                      school_authority_id: id,
                      contacts: [
                        {
                          user_id: adminUserId,
                          email: (raw.contactEmail || '').trim(),
                          title: (raw.contactName || '').trim() || 'Admin',
                          phone: (raw.contactPhone || '').trim() || null,
                        },
                      ],
                    },
                  ],
                })
              )
            : Promise.resolve(),
        ]);
        await firstValueFrom(this.schoolAuthorityApiService.publish(id)).catch(
          () => null
        );

        if (me) {
          const fullName =
            (me.name || '').trim() ||
            (raw?.contactName || '').trim() ||
            'Admin';
          await firstValueFrom(
            this.streamUserService.createUser({
              name: fullName,
              username: fullName,
              language: 'de',
            })
          ).catch(() => null);
        }
      }

      this.profileService.clearCurrentUserCache();
      await firstValueFrom(this.roleService.loadRole()).catch(() => null);
      await refreshFirebaseIdToken(this.auth).catch(() => null);
    } catch (e) {
      console.warn('Background upgrade tasks encountered an error:', e);
    }
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** `offers` (page 0) + Fachkraft `upgradeOffers` — same layout shell as the Angebot step. */
  get isOfferSelectionView(): boolean {
    return this.view === 'offers' || this.view === 'upgradeOffers';
  }

  /**
   * Role for {@link OrderProcessProductsComponent}: matches school onboarding & Fachkraft upgrade
   * (star banner, green ticks, copy). Plain dialog `offers` = Schuleinheit; upgrade = chosen role.
   */
  orderProcessProductsRole(): UpgradeRole {
    if (this.view === 'offers') {
      return 'schuleinheit';
    }
    return this.upgradeRole ?? 'schuleinheit';
  }

  /**
   * Schuleinheit plan step: reuse the onboarding «Angebot» UI ({@link SchoolUnitOffersComponent}, e.g. 6e2510f)
   * when Kostenlos + paid must both appear (Fachkraft/new flow, plain `offers`).
   * Paid-only single card when upgrading an existing free Schuleinheit (sidebar)—no free tier row.
   */
  shouldEmbedSchoolUnitOnboardingOffers(): boolean {
    return (
      this.orderProcessProductsRole() === 'schuleinheit' &&
      !this.existingSchuleinheitSchoolId
    );
  }

  get showBackIcon(): boolean {
    if (
      this.view === 'memberBlocked' ||
      this.view === 'offers' ||
      this.view === 'roleSelect' ||
      this.view === 'upgradeOrderSuccess'
    )
      return false;
    if (this.view === 'upgradeOffers') {
      // Schuleinheit-Upgrade aus dem In-App-Einstieg (bestehende Schule): die
      // Angebots-View ist der erste Screen, kein Zurück möglich.
      if (
        this.upgradeRole === 'schuleinheit' &&
        this.existingSchuleinheitSchoolId
      ) {
        return false;
      }
      // Schulgemeinde-Upgrade nach License-Order: hier ist kein Zurück sinnvoll.
      if (this.upgradeRole === 'schulgemeinde' && this.createdId != null) {
        return false;
      }
      // Sonst: Fresh Fachkraft-Upgrade — Back-Button anzeigen (Schuleinheit
      // führt zurück auf upgradeSummary, Schulgemeinde auf roleSelect).
      return true;
    }
    if (this.view === 'ordering' && this.stepperContainer) {
      return this.stepperContainer.getCurrentStep() !== 1;
    }
    return true;
  }
}
